<template>
  <div class="musubi-page musubi-page--messages">
    <header class="musubi-page__head">
      <h1>Send history</h1>
      <p class="text-muted">
        Every email Yorlin has queued or sent, newest first. Yorlin sends
        queued emails automatically in the background. Use Refresh to check
        on progress.
        <span v-if="testTools">Send a test email confirms delivery to one address.</span>
        <span v-else>To try out a campaign, open it and use Send a test.</span>
      </p>
    </header>

    <!-- Status stat tiles — one per row state -->
    <section class="stat-tiles">
      <div
        v-for="bucket in statBuckets"
        :key="bucket.key"
        :class="['stat-tile', `stat-tile--${bucket.tone}`]"
        :title="bucket.title"
      >
        <div class="stat-tile__value">{{ stats[bucket.key] ?? 0 }}</div>
        <div class="stat-tile__label">{{ bucket.label }}</div>
      </div>
    </section>

    <!-- Engagement tiles — populated as delivery-provider events arrive -->
    <section class="stat-tiles stat-tiles--engagement">
      <div class="stat-tile stat-tile--engage" :title="'All open events (one message can open multiple times)'">
        <div class="stat-tile__value">{{ stats.total_opens ?? 0 }}</div>
        <div class="stat-tile__label">Opens (total)</div>
      </div>
      <div class="stat-tile stat-tile--engage" :title="'Distinct messages that were opened at least once'">
        <div class="stat-tile__value">{{ stats.unique_opens ?? 0 }}</div>
        <div class="stat-tile__label">Opens (unique)</div>
      </div>
      <div class="stat-tile stat-tile--engage" :title="'All click events'">
        <div class="stat-tile__value">{{ stats.total_clicks ?? 0 }}</div>
        <div class="stat-tile__label">Clicks (total)</div>
      </div>
      <div class="stat-tile stat-tile--engage" :title="'Distinct messages with at least one click'">
        <div class="stat-tile__value">{{ stats.unique_clicks ?? 0 }}</div>
        <div class="stat-tile__label">Clicks (unique)</div>
      </div>
      <div class="stat-tile stat-tile--engage" :title="'Click-through rate: unique clicks ÷ delivered'">
        <div class="stat-tile__value">{{ ctrPct }}<span class="stat-tile__unit">%</span></div>
        <div class="stat-tile__label">CTR</div>
      </div>
    </section>

    <!-- Quick-action toolbar -->
    <section class="actions-bar">
      <button
        v-if="testTools"
        class="btn btn-outline-secondary"
        :disabled="busy.tick"
        title="Development only — forces one send pass right now instead of waiting for the automatic background sender."
        @click="tickQueue"
      >
        <CygnusIcon name="spinner" :spin="true" v-if="busy.tick" />
        <span v-else>Send queued now (dev)</span>
      </button>
      <button v-if="testTools" class="btn btn-outline-secondary" @click="showTestForm = !showTestForm">
        <span v-if="showTestForm">Hide test panel</span>
        <span v-else>Send a test email…</span>
      </button>
      <button class="btn btn-link" :disabled="busy.load" @click="refresh">
        <CygnusIcon name="spinner" :spin="true" v-if="busy.load" />
        <span v-else><CygnusIcon name="refresh" /> Refresh</span>
      </button>
      <div v-if="lastTickMessage" class="actions-bar__msg">{{ lastTickMessage }}</div>
    </section>

    <!-- Test-send-through-queue panel (collapsed by default) -->
    <section v-if="testTools && showTestForm" class="test-form">
      <h2 class="test-form__heading">Send a test email</h2>
      <p class="text-muted">
        Queues one message and sends it right away, so you can confirm
        delivery to a real inbox.
      </p>
      <form class="test-form__row" @submit.prevent="queueTest">
        <input v-model="testForm.to" type="email" required class="form-control" placeholder="recipient@example.com">
        <input v-model="testForm.subject" type="text" class="form-control" placeholder="Subject">
        <button type="submit" class="btn btn-primary" :disabled="busy.queue || !testForm.to">
          <CygnusIcon name="spinner" :spin="true" v-if="busy.queue" />
          <span v-else>Send test</span>
        </button>
      </form>
      <p v-if="lastQueueMessage" :class="['test-form__msg', queueOk ? 'is-ok' : 'is-error']">
        {{ lastQueueMessage }}
      </p>
    </section>

    <!-- Tabulator list -->
    <MusubiTabulator
      :rows="rows"
      :columns="columns"
      storage-key="admin.messages"
      :initial-sort="{ column: 'scheduled_at', dir: 'desc' }"
      :page-size="25"
      :loading="busy.load"
      :error="error"
    />
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiTabulator from "@root/components/MusubiTabulator.vue";
import { esc } from "./util/cellHtml";

export default {
  name: 'AdminMessages',
  components: { MusubiTabulator },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      rows: [],
      stats: {},
      busy: { load: false, tick: false, queue: false },
      error: null,
      lastTickMessage: '',
      lastQueueMessage: '',
      queueOk: false,
      showTestForm: false,
      // Whether this user may use the test and send-now tools (Yorlin staff);
      // the stats response says so. Hidden until it does.
      testTools: false,
      testForm: { to: '', subject: 'Yorlin queue test' },
    };
  },
  computed: {
    columns() {
      return [
        { key: 'id',           label: 'ID',        width: 70,  sorter: 'number' },
        { key: 'to_email',     label: 'To' },
        { key: 'subject',      label: 'Subject' },
        { key: 'status',       label: 'Status',    width: 110,
          format: (v) => `<span class="status-pill status-pill--${esc(v)}">${esc(v)}</span>` },
        // P2.5 — provider tracking counters. Both columns are 0 until
        // the Mailgun webhook fires the first open/click event.
        { key: 'opens_count',  label: 'Opens',     width: 80,  sorter: 'number',
          format: (v) => v > 0 ? `<span class="engage-cell engage-cell--opens">${esc(v)}</span>` : '<span class="engage-cell engage-cell--zero">0</span>' },
        { key: 'clicks_count', label: 'Clicks',    width: 80,  sorter: 'number',
          format: (v) => v > 0 ? `<span class="engage-cell engage-cell--clicks">${esc(v)}</span>` : '<span class="engage-cell engage-cell--zero">0</span>' },
        { key: 'scheduled_at', label: 'Scheduled', width: 140, sorter: 'number',
          format: (v) => v ? new Date(v * 1000).toLocaleString() : '—' },
        { key: 'sent_at',      label: 'Sent',      width: 140, sorter: 'number',
          format: (v) => v ? new Date(v * 1000).toLocaleString() : '—' },
      ];
    },
    ctrPct() {
      const delivered = Number(this.stats.delivered ?? 0);
      const clicks    = Number(this.stats.unique_clicks ?? 0);
      if (delivered === 0) return '0.0';
      return ((clicks / delivered) * 100).toFixed(1);
    },
    statBuckets() {
      return [
        { key: 'queued',     label: 'Queued',     tone: 'wait',  title: 'Waiting to send' },
        { key: 'sending',    label: 'Sending',    tone: 'wait',  title: 'On its way out right now' },
        { key: 'sent',       label: 'Sent',       tone: 'ok',    title: 'Handed off to the delivery provider' },
        { key: 'delivered',  label: 'Delivered',  tone: 'ok',    title: 'Confirmed delivered to the recipient’s inbox' },
        { key: 'bounced',    label: 'Bounced',    tone: 'warn',  title: 'Hard or soft bounce' },
        { key: 'complained', label: 'Complaint',  tone: 'warn',  title: 'Spam / abuse report' },
        { key: 'failed',     label: 'Failed',     tone: 'error', title: 'Permanent failure (incl. attempt-cap exhaustion)' },
        { key: 'dropped',    label: 'Dropped',    tone: 'error', title: 'Provider rejected pre-send' },
      ];
    },
  },
  mounted() {
    const remembered = (typeof localStorage !== 'undefined') && localStorage.getItem('musubi.queue.test-to');
    if (remembered) this.testForm.to = remembered;
    this.refresh();
  },
  methods: {
    async refresh() {
      this.busy.load = true;
      this.error = null;
      try {
        const [rowsRes, statsRes] = await Promise.all([
          this.api.get('/api/v1/admin/messages'),
          this.api.get('/api/v1/admin/queue/stats'),
        ]);
        const rowsPayload  = rowsRes?.data?.data ?? rowsRes?.data ?? rowsRes;
        const statsPayload = statsRes?.data?.stats ?? statsRes?.stats ?? statsRes?.data ?? statsRes;
        this.rows  = Array.isArray(rowsPayload) ? rowsPayload : (rowsPayload?.rows || []);
        this.stats = (statsPayload && typeof statsPayload === 'object') ? statsPayload : {};
        this.testTools = (statsRes?.data?.test_tools ?? statsRes?.test_tools) === true;
      } catch (e) {
        this.error = e?.message || 'Failed to load.';
      } finally {
        this.busy.load = false;
      }
    },

    async tickQueue() {
      this.busy.tick = true;
      this.lastTickMessage = '';
      try {
        const res = await this.api.post('/api/v1/admin/queue/tick', {});
        const payload = res?.data ?? res;
        const processed = payload?.processed ?? 0;
        this.lastTickMessage = `Processed ${processed} job${processed === 1 ? '' : 's'}.`;
        await this.refresh();
      } catch (e) {
        this.lastTickMessage = e?.response?.data?.error || e?.message || 'Tick failed.';
      } finally {
        this.busy.tick = false;
      }
    },

    async queueTest() {
      this.busy.queue = true;
      this.lastQueueMessage = '';
      this.queueOk = false;
      try { localStorage.setItem('musubi.queue.test-to', this.testForm.to); } catch (_) {}
      try {
        const queueRes = await this.api.post('/api/v1/admin/email/test-queue', {
          to: this.testForm.to,
          subject: this.testForm.subject,
        });
        const qPayload = queueRes?.data ?? queueRes;
        if (qPayload?.success) {
          // Tick immediately so user sees the row update in seconds
          await this.api.post('/api/v1/admin/queue/tick', {});
          this.queueOk = true;
          this.lastQueueMessage = `Queued message #${qPayload.message_id} + ran tick. Check the table below.`;
          await this.refresh();
        } else {
          this.lastQueueMessage = qPayload?.error || 'Queue failed.';
        }
      } catch (e) {
        this.lastQueueMessage = e?.response?.data?.error || e?.message || 'Queue failed.';
      } finally {
        this.busy.queue = false;
      }
    },
  },
};
</script>

<style>
.musubi-page--messages { padding: var(--m-space-6, 32px); }

.stat-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
  margin-bottom: 18px;
}
.stat-tile {
  background: var(--m-bg-card, #FFF);
  border: 1px solid var(--m-border, #E2E8F0);
  border-radius: var(--m-radius, 6px);
  padding: 12px 14px;
  border-left-width: 3px;
}
.stat-tile--wait   { border-left-color: var(--m-status-info, #3B82F6); }
.stat-tile--ok     { border-left-color: var(--m-status-success, #10B981); }
.stat-tile--warn   { border-left-color: var(--m-status-warning, #F59E0B); }
.stat-tile--error  { border-left-color: var(--m-status-danger, #EF4444); }
.stat-tile--engage { border-left-color: var(--m-accent, #8B5CF6); }
.stat-tile__value  { font-size: 1.3rem; font-weight: 600; line-height: 1; }
.stat-tile__unit   { font-size: 0.7rem; font-weight: 500; color: var(--m-text-secondary, #475569); margin-left: 2px; }
.stat-tiles--engagement { margin-top: -8px; }
.stat-tile__label {
  margin-top: 4px;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--m-text-secondary, #475569);
}

.actions-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.actions-bar__msg {
  font-size: 0.85rem;
  color: var(--m-text-secondary, #475569);
  margin-left: 8px;
}

.test-form {
  background: var(--m-bg-card, #FFF);
  border: 1px solid var(--m-border, #E2E8F0);
  border-radius: var(--m-radius-lg, 14px);
  box-shadow: var(--m-shadow-card);
  padding: 16px 18px;
  margin-bottom: 18px;
}
.test-form__heading {
  margin: 0 0 6px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--m-text-tertiary, #94A3B8);
}
.test-form .text-muted { font-size: 0.83rem; color: var(--m-text-secondary, #475569); margin: 0 0 10px; }
.test-form__row { display: flex; gap: 8px; align-items: center; }
.test-form__row .form-control { flex: 1; }
.test-form__msg { font-size: 0.85rem; margin: 8px 0 0; }
.test-form__msg.is-ok { color: var(--m-status-success, #10B981); }
.test-form__msg.is-error { color: var(--m-status-danger, #EF4444); }

/* Status pills inside Tabulator cells */
.status-pill {
  display: inline-block;
  padding: 2px 8px;
  font-size: 0.72rem;
  font-weight: 500;
  border-radius: 100px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.status-pill--queued     { background: #DBEAFE; color: #1E40AF; }
.status-pill--sending    { background: #FEF3C7; color: #92400E; }
.status-pill--sent       { background: #DCFCE7; color: #166534; }
.status-pill--delivered  { background: #DCFCE7; color: #166534; }
.status-pill--bounced    { background: #FFEDD5; color: #9A3412; }
.status-pill--complained { background: #FFE4E6; color: #9F1239; }
.status-pill--failed     { background: #FEE2E2; color: #991B1B; }
.status-pill--dropped    { background: #FEE2E2; color: #991B1B; }

/* Engagement counter cells */
.engage-cell {
  display: inline-block;
  min-width: 28px;
  text-align: center;
  padding: 1px 6px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 100px;
}
.engage-cell--opens  { background: #E0E7FF; color: #3730A3; }
.engage-cell--clicks { background: #D1FAE5; color: #065F46; }
.engage-cell--zero   { background: transparent; color: #94A3B8; font-weight: 400; }
</style>
